import React from "react";
import { Card, Form, Row, Col, Alert } from 'react-bootstrap';
import { connect } from "react-redux";
import { ValidatableForm } from "../components/ValidatableForm";
import { ValidatableFormGroup } from "../components/ValidatableFormGroup";
import { QUOTE_STEPS, COMPONENTS, QUOTE_QUESTIONS } from "../AppConstants";
import { validationUtils } from "../validation/Validation";
import { IQuoteStepState, IQuoteStepComponentProps } from "../components/interfaces";
import Utilities from "../classes/Utilities";
import ButtonContinue from "../components/ButtonContinue";
import { Button } from 'react-bootstrap';
import { IQuoteApiClient, ILOBApiClient, IPaths, ICoverage } from "../classes/Domain";
import QuoteActions from "../state/appActions";
import { IVehicle, ICoverageConfig, IDriver, IViolationList, IContactInfo, IDiscountOption } from "../classes/Domain";
import VehicleSelectByStep from "../components/VehicleSelectByStep";
import DriverPart from "../components/DriverPart";
import ViolationPart from "../components/ViolationPart";
import QuoteStepBase from '../components/QuoteStepBase';
import QuoteStep from "./QuoteStepLayout";
import { IAppState } from "../state/interfaces";
import SwiperCore, { Pagination } from 'swiper';
import ReactLoading from 'react-loading';
import { Rifm } from 'rifm';
import moment from "moment";

interface IAppStateUpdate {
    quoteUid: string,
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle>,
    coverageConfig: ICoverageConfig,
    contactInfo: IContactInfo,
    discounts: IDiscountOption
}

interface IState extends IQuoteStepState {
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle | null>,
    coverageConfig: ICoverageConfig,
    activeVehicleIndex: number,
    canAddNewVehicle: boolean,
    canAddNewDriver: boolean,
    continueClick: boolean,
    loadingViolationsList: boolean,
    contactInfo: IContactInfo,
    discounts: IDiscountOption
}

export interface IProps extends IQuoteStepComponentProps {
    stateCode: string,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle>,
    violationList: IViolationList,
    rateError: string | null,
    PATHS: IPaths,
    updateViolationList: (update: any) => Promise<any>;
    getQuickVehicleCoverages: () => Array<ICoverage>;
}

class Quick extends QuoteStepBase<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.next = this.next.bind(this);
        this.onVehicleAdd = this.onVehicleAdd.bind(this);
        this.onVehicleChange = this.onVehicleChange.bind(this);
        this.onVehicleDelete = this.onVehicleDelete.bind(this);
        this.onDriverAdd = this.onDriverAdd.bind(this);
        this.onDriverDelete = this.onDriverDelete.bind(this);
        this.onDriverChange = this.onDriverChange.bind(this);

        SwiperCore.use([Pagination]);

        this.state = {
            drivers: [{} as IDriver],
            vehicles: [null],
            coverageConfig: {
                liabilityCoverages: [
                    { code: 'BI', value: '25/50', isSelected: true },
                    { code: 'PD', value: '25', isSelected: true }],
                vehicleCoverages: [{
                    vehicleId: 0,
                    coverages: this.props.getQuickVehicleCoverages()
                }]
            } as ICoverageConfig,
            activeVehicleIndex: 0,
            canAddNewVehicle: false,
            canAddNewDriver: false,
            continueClick: false,
            contactInfo: { phone: '', email: '' } as IContactInfo,
            discounts: {
                installments: true,
                paperless: true
            } as IDiscountOption,
            updatingStore: false,
            loadingViolationsList: false,
            errors: null
        };
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.reportCurrentStep();

        if (!this.props.violationList) {
            this.setState({ ...this.state, loadingViolationsList: true }, () => {
                this.props.lobApiClient.getViolationList(this.props.stateCode)
                    .then((response) => {
                        this.props.updateViolationList(response.data)
                            .then(() => this.setState({ ...this.state, loadingViolationsList: false }));
                    });
            });
        }
    }

    subscribeToValidation() {
        validationUtils.subscribeToValidationFailure(QUOTE_STEPS.QUICK,
            validationData => {
                this.setState({ ...this.state, updatingStore: false, continueClick: false, errors: validationData });
            });
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.meta.propsSource === COMPONENTS.PROPS_SOURCES.APP_STORE) {
            nextProps.meta.propsSource = "";
            const vehicles = nextProps.vehicles;
            const drivers = nextProps.drivers;
            const coverageConfig = nextProps.coverageConfig;
            const contactInfo = nextProps.contactInfo;
            const discounts = nextProps.discounts;
            const initialSlide = nextProps.initialSlide;


            if (vehicles || drivers)
            {
                let canAddNewVehicle = nextProps.canAddNewVehicle;
                if (!canAddNewVehicle && vehicles.length > 0 && vehicles.every(x => x != null)) {
                    canAddNewVehicle = true;
                }

                let canAddNewDriver = nextProps.canAddNewDriver;
                if (!canAddNewDriver && drivers.length > 0 && drivers.every(x => x.age > 0 && x.maritalStatus && x.gender)) {
                    canAddNewDriver = true;
                }

                const nextState = {
                    ...prevState,
                    vehicles: vehicles,
                    canAddNewVehicle: canAddNewVehicle ?? prevState.canAddNewVehicle,
                    drivers: drivers,
                    canAddNewDriver: canAddNewDriver ?? prevState.canAddNewDriver,
                    coverageConfig: coverageConfig ?? prevState.coverageConfig,
                    contactInfo: contactInfo ?? prevState.contactInfo,
                    discounts: discounts ?? prevState.discounts,
                    initialSlide: initialSlide
                };

                return nextState;
            }
        }

        return prevState;
    }

    componentWillUnmount() {
        validationUtils.unsubscribeFromValidationFailure(QUOTE_STEPS.QUICK);
    }

    onDriverAdd() {
        let _drivers = this.state.drivers;
        _drivers.push({} as IDriver);

        this.setState({
            ...this.state, drivers: _drivers, canAddNewDriver: false, errors: null
        });
    }

    onDriverDelete = (index: number) => {
        let _drivers = this.state.drivers;
        _drivers.splice(index, 1);

        let _canAddNew = false;

        for (let d of _drivers) {
            if (d && d.age > 0 && d.maritalStatus && d.gender) {
                _canAddNew = true;
            }
            else {
                _canAddNew = false;
            }

            if (!_canAddNew) {
                break;
            }
        }

        this.setState({
            ...this.state, drivers: _drivers, canAddNewDriver: _canAddNew, errors: null
        });
    }

    onDriverChange(driver: IDriver, index: number) {
        let _drivers = this.state.drivers;
        driver.birthDate = moment(new Date()).add(-driver.age, 'y').format('MM/DD/YYYY');
        _drivers[index] = driver;

        if (driver && driver.age > 0 && driver.maritalStatus && driver.gender) {
            this.setState({ ...this.state, drivers: _drivers, canAddNewDriver: true });
        }
        else {
            this.setState({ ...this.state, drivers: _drivers, canAddNewDriver: false });
        }
    }

    onVehicleAdd() {
        let _vehicles = this.state.vehicles;
        _vehicles.push(null);

        let _vehicleCoverages = this.state.coverageConfig.vehicleCoverages;
        _vehicleCoverages.push({ vehicleId: _vehicles.length - 1, vehicleDescription: '', coverages: this.props.getQuickVehicleCoverages() });

        this.setState({
            ...this.state, vehicles: _vehicles, activeVehicleIndex: _vehicles.length - 1, canAddNewVehicle: false,
            coverageConfig: { ...this.state.coverageConfig, vehicleCoverages: _vehicleCoverages },
            errors: null
        });
    }

    onVehicleDelete = (index: number) => {
        let _vehicles = this.state.vehicles;
        _vehicles.splice(index, 1);

        let _vehicleCoverages = this.state.coverageConfig.vehicleCoverages;
        _vehicleCoverages.splice(index, 1);

        let _canAddNew = false;

        for (let v of _vehicles) {
            if (v && v.cylinders && v.cylinders > 0) {
                _canAddNew = true;
            }
            else {
                _canAddNew = false;
            }

            if (!_canAddNew) {
                break;
            }
        }

        this.setState({
            ...this.state, vehicles: _vehicles, canAddNewVehicle: _canAddNew,
            coverageConfig: { ...this.state.coverageConfig, vehicleCoverages: _vehicleCoverages },
        });
    }

    onVehicleChange(vehicle: IVehicle, vehicleIndex: number) {
        let _vehicles = this.state.vehicles;
        let _vehicleCoverages = this.state.coverageConfig.vehicleCoverages;

        const _vehicle = _vehicles[vehicleIndex];
        let _vehicleId = vehicleIndex;

        if (_vehicle && _vehicle.id > 0) {
            vehicle.id = _vehicle.id;
            _vehicleId = _vehicle.id;
        }

        for (let i = 0; i < _vehicleCoverages.length; i++) {
            if (_vehicleCoverages[i].vehicleId === _vehicleId) {
                _vehicleCoverages[i].coverages = vehicle.coverages ?? [];
            }
        }

        _vehicles[vehicleIndex] = vehicle;

        this.setState({
            ...this.state, vehicles: _vehicles,
            coverageConfig: { ...this.state.coverageConfig, vehicleCoverages: _vehicleCoverages },
            activeVehicleIndex: vehicleIndex, canAddNewVehicle: vehicle?.cylinders > 0
        });
    }

    next(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        event.preventDefault();

        this.setState({ updatingStore: true }, () => {
            const updatedVehicles = [...this.state.vehicles];
            const updatedDrivers = [...this.state.drivers];
            const coverageConfig = this.state.coverageConfig;
            const discounts = this.state.discounts;
            const contactInfo = this.state.contactInfo;

            this.props.updateAppState({ quoteUid: this.props.quoteUid, vehicles: updatedVehicles, drivers: updatedDrivers, coverageConfig: coverageConfig, discounts: discounts, contactInfo: contactInfo } as IAppStateUpdate)
                .then(() => this.props.history.push(this.props.PATHS.RESULT),
                    () => {
                        this.setState({ ...this.state, updatingStore: false });
                    }
                );
        });
    }

    handleInstallments = (evt: any) => {
        const _discounts = { ...this.state.discounts, installments: evt.target.checked };
        this.setState({ ...this.state, discounts: _discounts });
    };

    handleHomeOwner = (evt: any) => {
        const _discounts = { ...this.state.discounts, homeOwner: evt.target.checked };
        this.setState({ ...this.state, discounts: _discounts });
    };

    handlePaperless = (evt: any) => {
        const _discounts = { ...this.state.discounts, paperless: evt.target.checked };
        this.setState({ ...this.state, discounts: _discounts });
    };

    handlePriorInsurance = (evt: any) => {
        const _discounts = { ...this.state.discounts, priorInsurance: evt.target.checked };
        this.setState({ ...this.state, discounts: _discounts });
    };

    handleInsuredIsDefensiveDriver = (evt: any) => {
        const _discounts = { ...this.state.discounts, insuredIsDefensiveDriver: evt.target.checked };
        this.setState({ ...this.state, discounts: _discounts });
    };

    render() {
        return (
            <QuoteStep id={this.props.id} stateCode={this.props.stateCode} >
                <div className="titleArea"><h5>Quick Quote</h5></div>
                {
                    this.state.loadingViolationsList
                        ? <div className={"w-100 loading-indicator"}><ReactLoading type={"spin"} color={"#34B629"} height={'40px'} width={'40px'} /></div>
                        : null
                }
                {
                    this.props.rateError
                        ?
                        <div>
                            <Alert variant={"warning"}>
                                <h5 className="text-center">
                                    <strong className="text-uppercase">Sorry we can't continue</strong>
                                    <br />
                                    {this.props.rateError}
                                </h5>
                            </Alert>
                        </div>
                        :
                        null
                }
                {
                    !this.state.loadingViolationsList
                        ?
                        <div>
                            <p>Please enter all drivers and vehicles you wish to include on this policy.</p>
                            <Card className="quick-quote-data">
                                <Card.Body>
                                    <Card className="vehicles-card">
                                        <Card.Body>
                                            <Card.Title>
                                                {"VEHICLES"}
                                            </Card.Title>
                                            <ValidatableForm hasErrors={null != this.state.errors}>
                                                {
                                                    this.state.vehicles.map((vehicle, vehicleIndex) => (
                                                        <VehicleSelectByStep
                                                            key={Utilities.uniqueId()}
                                                            updatingStore={this.state.updatingStore}
                                                            quoteApiClient={this.props.quoteApiClient}
                                                            quoteId={this.props.quoteId}
                                                            quoteUid={this.props.quoteUid}
                                                            vehicle={vehicle}
                                                            vehicleIndex={vehicleIndex}
                                                            isActive={this.state.activeVehicleIndex === vehicleIndex}
                                                            onVehicleChange={this.onVehicleChange}
                                                            onVehicleDelete={this.onVehicleDelete}
                                                            getQuickVehicleCoverages={this.props.getQuickVehicleCoverages}
                                                            errors={this.state.errors}
                                                        />
                                                    ))
                                                }
                                                <div className="btnContainer">
                                                    <Button
                                                        className="btnAddVehicle"
                                                        onClick={() => { this.onVehicleAdd() }}
                                                        disabled={this.state.updatingStore || !this.state.canAddNewVehicle || this.state.vehicles?.length === 4}> <i className="fas fa-plus"></i> Add vehicle
                                                    </Button>
                                                </div>
                                            </ValidatableForm>
                                        </Card.Body>
                                    </Card>
                                    <div className="break-2" />
                                    <Card className="drivers-card">
                                        <Card.Body>
                                            <Card.Title>
                                                {"DRIVERS"}
                                            </Card.Title>
                                            <ValidatableForm hasErrors={null != this.state.errors}>
                                                {
                                                    this.state.drivers.map((driver, driverIndex) => (
                                                        <DriverPart
                                                            key={Utilities.uniqueId()}
                                                            updatingStore={this.state.updatingStore}
                                                            driver={driver}
                                                            driverIndex={driverIndex}
                                                            onDriverChange={this.onDriverChange}
                                                            onDriverDelete={this.onDriverDelete}
                                                            violationList={this.props.violationList}
                                                            errors={this.state.errors}
                                                        />
                                                    ))
                                                }
                                                <div className="btnContainer">
                                                    <Button
                                                        className="btnAddDriver"
                                                        onClick={() => { this.onDriverAdd() }}
                                                        disabled={this.state.updatingStore || !this.state.canAddNewDriver}> <i className="fas fa-plus"></i> Add driver
                                                    </Button>
                                                </div>
                                            </ValidatableForm>
                                        </Card.Body>
                                    </Card>
                                    {
                                        this.state.drivers.some(x => x.accidentsViolations === QUOTE_QUESTIONS.YES_NO.YES)
                                            ?
                                            <div>
                                                <div className="break-2" />
                                                <Card className="drivers-card">
                                                    <Card.Body>
                                                        <Card.Title>
                                                            {"VIOLATIONS/ACCIDENTS"}
                                                        </Card.Title>
                                                        <ValidatableForm hasErrors={null != this.state.errors}>
                                                            {
                                                                this.state.drivers.filter(x => x.accidentsViolations === QUOTE_QUESTIONS.YES_NO.YES).map((driver, driverIndex) => (
                                                                    <div key={Utilities.uniqueId()}>
                                                                        <Card className="violations-card">
                                                                            <Card.Body>
                                                                                <Card.Title>
                                                                                    {'Driver #' + (this.state.drivers.findIndex(x => x === driver) + 1)}
                                                                                </Card.Title>
                                                                                <Form.Group className="col vehicle-row">
                                                                                    <Row>
                                                                                        <Col sm={5} className="justify-content-center d-flex">Incident</Col>
                                                                                        <Col sm={3} className="justify-content-center d-flex">Violation Date</Col>
                                                                                        <Col sm={3} className="justify-content-center d-flex">Conviction Date</Col>
                                                                                        <Col sm={1} className="justify-content-center d-flex"></Col>
                                                                                    </Row>
                                                                                    <ViolationPart
                                                                                        key={Utilities.uniqueId()}
                                                                                        updatingStore={this.state.updatingStore}
                                                                                        driver={driver}
                                                                                        driverIndex={this.state.drivers.findIndex(x => x === driver)}
                                                                                        violationList={this.props.violationList}
                                                                                        onDriverChange={this.onDriverChange}
                                                                                        errors={this.state.errors}
                                                                                    />
                                                                                </Form.Group>
                                                                            </Card.Body>
                                                                        </Card>
                                                                        <div className="break-2" />
                                                                    </div>
                                                                ))
                                                            }
                                                        </ValidatableForm>
                                                    </Card.Body>
                                                </Card>
                                            </div>
                                            :
                                            null
                                    }
                                    <div className="break-2" />
                                    <Card className="drivers-card">
                                        <Card.Body>
                                            <Card.Title>
                                                {
                                                    'CONTACT'
                                                }
                                            </Card.Title>
                                            <ValidatableForm hasErrors={null != this.state.errors}>
                                                <Form.Group className="col">
                                                    <Row>
                                                        <Col sm={6}>
                                                            <Rifm
                                                                accept={/\d/g}
                                                                mask={10 <= this.state.contactInfo.phone.length}
                                                                format={Utilities.formatPhone}
                                                                value={this.state.contactInfo.phone}
                                                                onChange={(v) => { this.setStateValue(v, "phone", this.state.contactInfo) }}>
                                                                {({ value, onChange }) => (
                                                                    <ValidatableFormGroup
                                                                        disabled={this.state.updatingStore}
                                                                        label="Phone number"
                                                                        placeholder="(___)___-____"
                                                                        value={value}
                                                                        validated={null !== this.state.errors}
                                                                        validationError={validationUtils.getValidationErrorFor(this.state.errors, "contactInfo.phone")}
                                                                        onChange={(e) => { onChange(e) }}
                                                                        pattern="[0-9]*"
                                                                        inputmode="numeric" />
                                                                )}
                                                            </Rifm>
                                                        </Col>
                                                        <Col sm={6}>
                                                            {/*<ValidatableFormGroup*/}
                                                            {/*    disabled={this.state.updatingStore}*/}
                                                            {/*    label="Email address (*Optional)"*/}
                                                            {/*    value={this.state.contactInfo.email}*/}
                                                            {/*    placeholder="Enter email"*/}
                                                            {/*    validated={null !== this.state.errors}*/}
                                                            {/*    validationError={validationUtils.getValidationErrorFor(this.state.errors, "contactInfo.email")}*/}
                                                            {/*    onChange={(e) => { this.setStateValue(e.target.value, "email", this.state.contactInfo) }} />*/}
                                                        </Col>
                                                    </Row>
                                                </Form.Group>
                                            </ValidatableForm>
                                        </Card.Body>
                                    </Card>
                                    <div className="break-2" />
                                    <Card key={Utilities.uniqueId()} className="drivers-card">
                                        <Card.Body>
                                            <Card.Title>
                                                {
                                                    'DISCOUNT/OPTIONS'
                                                }
                                            </Card.Title>
                                            <ValidatableForm hasErrors={null != this.state.errors}>
                                                <Form.Group className="col">

                                                    <Form.Check type="checkbox" id="installments">
                                                        <Form.Check.Input
                                                            type="checkbox"
                                                            checked={this.state.discounts?.installments}
                                                            onChange={this.handleInstallments}
                                                            isValid />
                                                        <Form.Check.Label className="lable-grey">I will pay with automatic payments.</Form.Check.Label>
                                                    </Form.Check>

                                                    <Form.Check type="checkbox" id="homeOwner">
                                                        <Form.Check.Input
                                                            type="checkbox"
                                                            checked={this.state.discounts?.homeOwner}
                                                            onChange={this.handleHomeOwner}
                                                            isValid />
                                                        <Form.Check.Label className="lable-grey">I am a homeowner.</Form.Check.Label>
                                                    </Form.Check>

                                                    <Form.Check type="checkbox" id="paperless">
                                                        <Form.Check.Input
                                                            type="checkbox"
                                                            checked={this.state.discounts?.paperless}
                                                            onChange={this.handlePaperless}
                                                            isValid />
                                                        <Form.Check.Label className="lable-grey">I want to subscribe for paperless documents.</Form.Check.Label>
                                                    </Form.Check>

                                                    <Form.Check type="checkbox" id="priorInsurance">
                                                        <Form.Check.Input
                                                            type="checkbox"
                                                            checked={this.state.discounts?.priorInsurance}
                                                            onChange={this.handlePriorInsurance}
                                                            isValid />
                                                        <Form.Check.Label className="lable-grey">I currently have auto insurance through another company.</Form.Check.Label>
                                                    </Form.Check>

                                                    <Form.Check type="checkbox" id="insuredIsDefensiveDriver">
                                                        <Form.Check.Input
                                                            type="checkbox"
                                                            checked={this.state.discounts?.insuredIsDefensiveDriver}
                                                            onChange={this.handleInsuredIsDefensiveDriver}
                                                            isValid />
                                                        <Form.Check.Label className="lable-grey">Completed defensive driver course (In the past 6 months).</Form.Check.Label>
                                                    </Form.Check>
                                                </Form.Group>
                                            </ValidatableForm>
                                        </Card.Body>
                                    </Card>
                                </Card.Body>
                            </Card>

                            <div className="btnContainer">
                                <ButtonContinue
                                    label="Get Rate"
                                    disabled={this.state.updatingStore}
                                    showLoading={this.state.updatingStore}
                                    onClick={this.next} />
                            </div>
                        </div>
                        :
                        null
                }
            </QuoteStep>
        );
    }
}

let mapProps = (state: IAppState, ownProps: IProps) => {
    return {
        quoteUid: state.quoteUid,
        vehicles: state.vehicles.length > 0 ? state.vehicles : [null],
        coverageConfig: state.coverageConfig,
        drivers: state.drivers.length > 0 ? state.drivers : [{} as IDriver],
        violationList: state.violationList,
        contactInfo: state.contactInfo,
        discounts: state.discountOption,
        rateError: state.rateError,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE }
    };
};

let mapDispatch = (dispatch: any, ownProps: IProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(QUOTE_STEPS.QUICK)),
        updateAppState: (update: IAppStateUpdate) => dispatch(quoteActions.updateQuickData(update.quoteUid, update.vehicles, update.drivers, update.coverageConfig, update.discounts, update.contactInfo)),
        updateViolationList: (update: IViolationList) => dispatch(quoteActions.updateViolatioList(update)),
        getQuickVehicleCoverages: () => (quoteActions.GetQuickVehicleCoverages())
    };
}

export default connect(mapProps, mapDispatch)(Quick);